import { HORIZONTAL_SIDES, VERTICAL_SIDES } from 'ContentUtils/constants/Border';
import { ColorShadePositions } from 'ContentUtils/constants/ColorShadePositions';

// Requires verticalSide and/or horizontalSide

/**
 * Returns true if `alpha` is a valid decimal alpha value in the range [0, 1].
 */
export const isValidAlphaDec = alpha => {
  if (isNaN(alpha) || typeof alpha !== 'number' || alpha < 0 || alpha > 1) {
    return false;
  }
  return true;
};

/**
 * Returns true if `alpha` is a valid integer alpha value in the range [0, 100],
 * or null (null is treated as "no alpha specified" and is considered valid).
 */
export const isValidAlphaInt = alpha => {
  const isNotANumericAlpha = isNaN(alpha) || typeof alpha !== 'number';
  const isValidAlphaRange = alpha >= 0 && alpha <= 100;
  return alpha == null || !isNotANumericAlpha && isValidAlphaRange;
};

/**
 * Returns true if `colorChannels` is a valid RGB object with r, g, b each in [0, 255].
 */
export const isValidRGB = colorChannels => {
  const rgb = ['r', 'g', 'b'];
  // The RGB values all have the same valid range, check if any one of them is bad
  const hasValidRGBValues = rgb.every(channel => {
    if (isNaN(colorChannels[channel]) || typeof colorChannels[channel] !== 'number' || colorChannels[channel] < 0 || colorChannels[channel] > 255) {
      return false;
    }
    return true;
  });
  // If there are no bad RGB values and we made is this far, than this is a valid RGBA object
  if (hasValidRGBValues) {
    return true;
  } else {
    return false;
  }
};
const HSB_MAX_VALUES = {
  h: 360,
  s: 100,
  b: 100
};

/**
 * Returns true if `hsbObject` is a valid HSB color with h in [0, 360], s in [0, 100], b in [0, 100].
 */
export const isValidHsbObject = hsbObject => {
  const hasValidHsbValues = Object.keys(HSB_MAX_VALUES).every(key => {
    const actualValue = hsbObject[key];
    const maxValue = HSB_MAX_VALUES[key];
    return !isNaN(actualValue) && typeof actualValue === 'number' && actualValue >= 0 && actualValue <= maxValue;
  });
  return hasValidHsbValues;
};

/**
 * Returns true if `colorChannels` is a valid RGBA object.
 */
export const isValidRGBA = colorChannels => {
  return isValidAlphaDec(colorChannels.a) && isValidRGB(colorChannels);
};

/**
 * Returns true if `colorString` is a valid CSS `rgb(r, g, b)` string.
 */
export const isValidRgbString = colorString => {
  return /^rgb\((\d{1,3}%?),\s*(\d{1,3}%?),\s*(\d{1,3}%?)\)$/i.test(colorString);
};

/**
 * Returns true if `colorString` is a valid CSS `rgba(r, g, b, a)` string.
 */
export const isValidRgbaString = colorString => {
  return /^rgba\((\d{1,3}%?),\s*(\d{1,3}%?),\s*(\d{1,3}%?),\s*(\d*(?:\.\d+)?)\)$/i.test(colorString);
};

/**
 * Returns true if `color` is a string of 0–6 hex digits, optionally prefixed with `#`.
 * Accepts partial or incomplete hex codes — use `isValidHex` for full validation.
 */
export const isHexString = color => typeof color === 'string' && Boolean(color) && /^#?[0-9A-Fa-f]{0,6}$/.test(color);

/**
 * Returns true if `hex` is null or a fully valid 3- or 6-digit hex color string prefixed with `#`.
 */
export const isValidHex = hex => {
  return hex == null || /^#([0-9A-F]{6}|[0-9A-F]{3})$/i.test(hex);
};

/**
 * Returns true if `sideOrCornerObject` is a valid gradient direction.
 * Requires at least one of `verticalSide` or `horizontalSide`, and each must be a
 * recognized value from the `VERTICAL_SIDES` / `HORIZONTAL_SIDES` constants.
 */
export const isValidGradientDirection = sideOrCornerObject => {
  const {
    verticalSide,
    horizontalSide
  } = sideOrCornerObject;
  if (!verticalSide && !horizontalSide) {
    console.warn('Gradient sideOrCorner key must specify the keys "verticalSide" and/or "horizonalSide"');
    return false;
  }
  if (verticalSide && verticalSide !== VERTICAL_SIDES.TOP && verticalSide !== VERTICAL_SIDES.BOTTOM) {
    return false;
  }
  if (horizontalSide && horizontalSide !== HORIZONTAL_SIDES.LEFT && horizontalSide !== HORIZONTAL_SIDES.RIGHT) {
    return false;
  }
  return true;
};

/**
 * Converts a single 0–255 color channel integer to a 2-character lowercase hex string.
 * Zero-pads single-digit results (e.g., `5` → `"05"`).
 * @param color - Integer in [0, 255]
 * @returns 2-character hex string
 */
export const convertColorBitToHex = color => {
  let hex = Number(color).toString(16);
  if (hex.length < 2) hex = `0${hex}`;
  return hex;
};

/**
 * Converts an `RGBType` object to a 6-digit hex string (e.g., `"#ff0000"`).
 * Returns null if any channel is null or the result is not a valid hex.
 * @param rgb - Object with r, g, b channels in [0, 255]
 * @returns Hex string like `"#rrggbb"`, or null on invalid input
 */
export const convertRgbToHex = ({
  r,
  g,
  b
}) => {
  if (r == null || g == null || b == null) return null;
  const red = convertColorBitToHex(r);
  const green = convertColorBitToHex(g);
  const blue = convertColorBitToHex(b);
  const hex = `#${red}${green}${blue}`;
  return isValidHex(hex) ? hex : null;
};
const DEFAULT_ALPHA_INT = 100;

/**
 * Converts a decimal alpha value (0–1) to an integer alpha value (0–100).
 * Returns 100 if the input is not a valid decimal alpha.
 * @param alphaDec - Decimal alpha in [0, 1], or null
 * @returns Integer alpha in [0, 100]
 */
export const convertAlphaDecToInt = alphaDec => {
  const alphaInt = typeof alphaDec === 'number' ? alphaDec * DEFAULT_ALPHA_INT : DEFAULT_ALPHA_INT;
  return isValidAlphaInt(alphaInt) ? alphaInt : DEFAULT_ALPHA_INT;
};
const DEFAULT_ALPHA_DEC = 1.0;

/**
 * Converts an integer alpha value (0–100) to a decimal alpha value (0–1).
 * Returns 1.0 if the input is null or not a valid number.
 * @param alphaInt - Integer alpha in [0, 100], or null
 * @returns Decimal alpha in [0, 1]
 */
export const convertAlphaIntToDec = alphaInt => {
  if (alphaInt === null || typeof alphaInt !== 'number') {
    return DEFAULT_ALPHA_DEC;
  }
  const alphaDec = alphaInt / DEFAULT_ALPHA_INT;
  return isValidAlphaDec(alphaDec) ? alphaDec : DEFAULT_ALPHA_DEC;
};

/**
 * Converts an `RGBAType` object to `{ hex, alpha }` where hex is a 6-digit hex string
 * and alpha is an integer in [0, 100].
 * @param rgba - RGBA object with decimal alpha
 * @returns `{ hex: string | null, alpha: number }`
 */
export const convertRgbaToHexAndAlphaInt = ({
  r,
  g,
  b,
  a = 1.0
}) => {
  return {
    hex: convertRgbToHex({
      r,
      g,
      b
    }),
    alpha: convertAlphaDecToInt(a)
  };
};

/** Rounds an HSB value to two decimal places. */
const roundHsbValue = num => Math.round(num * 100) / 100;

/**
 * Converts an `RGBType` object to an `HSBType` object (Hue 0–360, Saturation 0–100, Brightness 0–100).
 * Returns null if the input is invalid.
 * Algorithm from https://www.30secondsofcode.org/js/s/rgb-hex-hsl-hsb-color-format-conversion/#rgb-to-hsb
 * @param rgbObject - RGB object with channels in [0, 255]
 * @returns HSB object, or null if input is invalid
 */
export const convertRgbToHsb = rgbObject => {
  if (!rgbObject || !isValidRGB(rgbObject)) {
    return null;
  }
  let {
    r,
    g,
    b
  } = rgbObject;
  r = r / 255;
  g = g / 255;
  b = b / 255;
  const maxValue = Math.max(r, g, b);
  const minMaxDiff = maxValue - Math.min(r, g, b);
  const h = minMaxDiff === 0 ? 0 : minMaxDiff && maxValue === r ? (g - b) / minMaxDiff : maxValue === g ? 2 + (b - r) / minMaxDiff : 4 + (r - g) / minMaxDiff;
  return {
    h: roundHsbValue(60 * (h < 0 ? h + 6 : h)),
    s: roundHsbValue(maxValue && minMaxDiff / maxValue * 100),
    b: roundHsbValue(maxValue * 100)
  };
};

/**
 * Converts an `HSBType` object (Hue 0–360, Saturation 0–100, Brightness 0–100) to an `RGBType` object.
 * Returns null if the input is invalid (e.g., any channel out of range).
 * Algorithm from https://www.30secondsofcode.org/js/s/rgb-hex-hsl-hsb-color-format-conversion/#hsb-to-rgb
 * @param hsbObject - HSB object with h in [0, 360], s and b in [0, 100]
 * @returns RGB object with channels in [0, 255], or null if input is invalid
 */
export const convertHsbToRgb = hsbObject => {
  if (!hsbObject || !isValidHsbObject(hsbObject)) {
    return null;
  }
  const {
    h
  } = hsbObject;
  let {
    s,
    b
  } = hsbObject;
  s = s / 100;
  b = b / 100;
  const convertHue = n => (n + h / 60) % 6;
  const convertToRgbValue = n => 255 * (b * (1 - s * Math.max(0, Math.min(convertHue(n), 4 - convertHue(n), 1))));
  return {
    r: Math.round(convertToRgbValue(5)),
    g: Math.round(convertToRgbValue(3)),
    b: Math.round(convertToRgbValue(1))
  };
};
const EMPTY_RGB_OBJECT = {
  r: null,
  b: null,
  g: null
};

/** Extracts the numeric channel values from a CSS `rgb()` or `rgba()` string. */
const getRbgNumberArray = rgbString => {
  var _rgbString$split$1$sp, _rgbString$split$;
  const rgbStringArray = (_rgbString$split$1$sp = (_rgbString$split$ = rgbString.split('(')[1]) === null || _rgbString$split$ === void 0 || (_rgbString$split$ = _rgbString$split$.split(')')[0]) === null || _rgbString$split$ === void 0 ? void 0 : _rgbString$split$.split(',')) !== null && _rgbString$split$1$sp !== void 0 ? _rgbString$split$1$sp : [];
  return rgbStringArray.map(stringNum => parseFloat(stringNum));
};

/**
 * Parses a CSS `rgb(r, g, b)` string into an `RGBType` object.
 * @param rgbString - CSS rgb string, e.g. `"rgb(255, 0, 0)"`
 * @returns RGB object, or `{ r: null, g: null, b: null }` if invalid
 */
export const convertRgbStringToRgbObject = rgbString => {
  var _rgbNumberArray$, _rgbNumberArray$2, _rgbNumberArray$3;
  if (typeof rgbString !== 'string') {
    return EMPTY_RGB_OBJECT;
  }
  const rgbNumberArray = getRbgNumberArray(rgbString);
  const rgbObject = {
    r: (_rgbNumberArray$ = rgbNumberArray[0]) !== null && _rgbNumberArray$ !== void 0 ? _rgbNumberArray$ : null,
    g: (_rgbNumberArray$2 = rgbNumberArray[1]) !== null && _rgbNumberArray$2 !== void 0 ? _rgbNumberArray$2 : null,
    b: (_rgbNumberArray$3 = rgbNumberArray[2]) !== null && _rgbNumberArray$3 !== void 0 ? _rgbNumberArray$3 : null
  };
  return isValidRGB(rgbObject) ? rgbObject : EMPTY_RGB_OBJECT;
};

/**
 * Parses a CSS `rgba(r, g, b, a)` string into an `RGBAType` object.
 * @param rgbaString - CSS rgba string, e.g. `"rgba(255, 0, 0, 0.5)"`
 * @returns RGBA object, or `{ r: null, g: null, b: null, a: null }` if invalid
 */
export const convertRgbaStringToRgbaObject = rgbaString => {
  var _rgbaNumberArray$, _rgbaNumberArray$2, _rgbaNumberArray$3;
  if (typeof rgbaString !== 'string') {
    return Object.assign({}, EMPTY_RGB_OBJECT, {
      a: null
    });
  }
  const rgbaNumberArray = getRbgNumberArray(rgbaString);
  const rgbaObject = {
    r: (_rgbaNumberArray$ = rgbaNumberArray[0]) !== null && _rgbaNumberArray$ !== void 0 ? _rgbaNumberArray$ : null,
    g: (_rgbaNumberArray$2 = rgbaNumberArray[1]) !== null && _rgbaNumberArray$2 !== void 0 ? _rgbaNumberArray$2 : null,
    b: (_rgbaNumberArray$3 = rgbaNumberArray[2]) !== null && _rgbaNumberArray$3 !== void 0 ? _rgbaNumberArray$3 : null,
    a: typeof rgbaNumberArray[3] === 'number' ? rgbaNumberArray[3] : 1
  };
  return isValidRGBA(rgbaObject) ? rgbaObject : Object.assign({}, EMPTY_RGB_OBJECT, {
    a: null
  });
};

/**
 * Converts a hex color string and an optional decimal alpha (0–1, default 1) to an `RGBAType` object.
 * Accepts both 3- and 6-digit hex strings prefixed with `#`.
 * @param hex - Hex color string, e.g. `"#ff0000"` or `"#f00"`
 * @param alpha - Decimal alpha in [0, 1], defaults to 1
 * @returns RGBA object, or `{ r: null, g: null, b: null, a: null }` if hex is invalid
 */
export const convertHexAndAlphaToRgbaObject = (hex, alpha = 1) => {
  if (!isValidHex(hex) || !hex) return Object.assign({}, EMPTY_RGB_OBJECT, {
    a: null
  });
  hex = hex.replace('#', '');

  // 3 characters is a valid hex, double it to 6 characters for the sake of
  // computed rgba values if needed. Changes "abc" to "aabbcc"
  if (hex.length === 3) {
    const sixDigitHex = hex.split('').reduce((accumulator, currentValue) => {
      return accumulator + currentValue + currentValue;
    }, '');
    hex = sixDigitHex;
  }
  const rgba = {
    r: parseInt(hex.substring(0, 2), 16),
    g: parseInt(hex.substring(2, 4), 16),
    b: parseInt(hex.substring(4, 6), 16),
    a: alpha
  };
  return isValidRGBA(rgba) ? rgba : Object.assign({}, EMPTY_RGB_OBJECT, {
    a: null
  });
};

/**
 * Converts a hex color string and an integer alpha (0–100, default 100) to an `RGBAType` object.
 * Normalizes the integer alpha to decimal before calling `convertHexAndAlphaToRgbaObject`.
 * @param hex - Hex color string
 * @param opacity - Integer alpha in [0, 100], defaults to 100
 * @returns RGBA object, or `{ r: null, g: null, b: null, a: null }` if hex is invalid
 */
export const convertHexAndAlphaIntToRgbaObject = (hex, opacity = 100) => {
  return convertHexAndAlphaToRgbaObject(hex, convertAlphaIntToDec(opacity));
};

/**
 * Shifts each RGB channel of a hex color by `brightnessOffset` (positive = lighter, negative = darker),
 * clamped to [0, 255]. This is a flat RGB shift and does not preserve hue or saturation.
 * @param hexColor - Hex color string
 * @param brightnessOffset - Amount to add to each RGB channel; positive lightens, negative darkens
 * @returns Adjusted hex color string, or the original if input is invalid
 */
export function adjustColorBrightness(hexColor, brightnessOffset) {
  if (!hexColor || hexColor.trim() === '') {
    return hexColor;
  }
  const rgbHex = convertHexAndAlphaToRgbaObject(hexColor);
  if (!rgbHex || !isValidRGBA(rgbHex)) {
    return hexColor;
  }
  rgbHex.r = Math.min(255, Math.max(0, rgbHex.r + brightnessOffset));
  rgbHex.g = Math.min(255, Math.max(0, rgbHex.g + brightnessOffset));
  rgbHex.b = Math.min(255, Math.max(0, rgbHex.b + brightnessOffset));
  return convertRgbToHex(rgbHex) || hexColor;
}

// Luma coefficients sourced from https://en.wikipedia.org/wiki/Rec._709#Luma_coefficients
const RED_LUMA_COEFFICIENT = 0.2126;
const GREEN_LUMA_COEFFICIENT = 0.7152;
const BLUE_LUMA_COEFFICIENT = 0.0722;

/**
 * Returns the perceived brightness of a hex color as a luma value in [0, 255],
 * using the Rec. 709 luma coefficients.
 * @param hexColor - Hex color string
 * @returns Luma value in [0, 255], or null if the input is invalid
 */
export const getPerceivedBrightness = hexColor => {
  if (!hexColor || hexColor.trim() === '' || !isValidHex(hexColor)) {
    return null;
  }
  const rgbHex = convertHexAndAlphaToRgbaObject(hexColor);
  if (!rgbHex || !isValidRGBA(rgbHex)) {
    return null;
  }
  const {
    r,
    g,
    b
  } = rgbHex;
  return RED_LUMA_COEFFICIENT * r + GREEN_LUMA_COEFFICIENT * g + BLUE_LUMA_COEFFICIENT * b;
};
const DARK_THRESHOLD = 50;
const LIGHT_THRESHOLD = 128;

/**
 * Returns true if `hexColor` is perceived as dark (luma below `darkThreshold`, default 50).
 * Based on Rec. 709 luma coefficients via `getPerceivedBrightness`.
 * @param hexColor - Hex color string
 * @param darkThreshold - Luma threshold below which a color is considered dark; defaults to 50
 */
export const isDark = (hexColor, darkThreshold = DARK_THRESHOLD) => {
  const perceivedBrightness = getPerceivedBrightness(hexColor);
  return perceivedBrightness !== null && perceivedBrightness < darkThreshold;
};

/**
 * Returns true if `hexColor` is perceived as light (luma above `lightThreshold`, default 128).
 * Based on Rec. 709 luma coefficients via `getPerceivedBrightness`.
 * @param hexColor - Hex color string
 * @param lightThreshold - Luma threshold above which a color is considered light; defaults to 128
 */
export const isLight = (hexColor, lightThreshold = LIGHT_THRESHOLD) => {
  const perceivedBrightness = getPerceivedBrightness(hexColor);
  return perceivedBrightness !== null && perceivedBrightness > lightThreshold;
};

// --- Shade scale algorithm (HSB-based, positional) ---
// Positions match the BE ColorShade enum exactly: LIGHTER=0, LIGHT=1, MEDIUM=2, DARK=3, DARKER=4

const NUM_POSITIONS = 5;
const LIGHTER_ANCHOR_THRESHOLD = 10;
const LIGHT_ANCHOR_THRESHOLD = 40;
const DARK_ANCHOR_THRESHOLD = 40;
const DARKER_ANCHOR_THRESHOLD = 10;
const BRIGHTNESS_FLOOR = 15;
const BRIGHTNESS_CEILING = 95;
const MAX_LIGHTER_B_STEP = 35;
const MAX_DARKER_B_STEP = 35;
const MAX_LIGHTER_S_STEP_FROM_MEDIUM = 35;
const MAX_LIGHTER_S_STEP_FROM_LIGHT = 75;
const MAX_DARKER_S_STEP = 20;

// Pulls vivid/saturated colors toward the center (50%) for anchor placement.
// At factor=0.6, a fully saturated color moves 60% of the way from its luma% toward 50%.
// This reflects that a highly saturated color is tonally "complete" and benefits from
// stops on both sides, unlike a light/dark neutral where luma alone is sufficient.
const SATURATION_CENTERING_FACTOR = 0.6;
const LIGHTER_SATURATION_STEP = -20;
const DARKER_SATURATION_STEP = 6;
const LOW_SAT_THRESHOLD = 5;
const SAT_FLOOR = 5;

/**
 * Returns the anchor index (0–4) for a color within the 5-stop shade scale.
 * The anchor is the position where the base color itself appears — all lighter
 * shades are to the left (lower index) and all darker shades are to the right.
 *
 * Placement is based on perceived luma, adjusted toward center for saturated colors
 * to ensure vivid hues get stops on both sides rather than being pushed to an extreme.
 *
 * Positions: 0=LIGHTER, 1=LIGHT, 2=MEDIUM, 3=DARK, 4=DARKER
 * @param hex - Hex color string
 * @returns Anchor index 0–4; defaults to 2 (MEDIUM) if luma cannot be computed
 */
export function getAnchorPosition(hex) {
  const luma = getPerceivedBrightness(hex);
  if (luma === null) return 2;
  const lumaPercent = luma / 255 * 100;
  const rgb = convertHexAndAlphaToRgbaObject(hex);
  const hsb = convertRgbToHsb(rgb);
  const saturation = hsb ? hsb.s / HSB_MAX_VALUES.s : 0;
  const b = lumaPercent + (50 - lumaPercent) * saturation * SATURATION_CENTERING_FACTOR;
  const lighterHeadroom = 100 - b;
  const darkerHeadroom = b;
  if (lighterHeadroom < LIGHTER_ANCHOR_THRESHOLD) return 0;
  if (darkerHeadroom < DARKER_ANCHOR_THRESHOLD) return 4;
  if (lighterHeadroom < LIGHT_ANCHOR_THRESHOLD) return 1;
  if (darkerHeadroom < DARK_ANCHOR_THRESHOLD) return 3;
  return 2;
}

/**
 * Computes the hex color for a single shade position in the 5-stop scale.
 *
 * Lighter shades (targetIndex < anchorIndex): brightness is raised toward a
 * saturation-dependent ceiling (high-saturation colors are allowed to reach B=100%),
 * and saturation is reduced. If the base color is already at the ceiling, lighter
 * stops are generated purely through saturation reduction.
 *
 * Darker shades (targetIndex > anchorIndex): brightness is lowered toward
 * `BRIGHTNESS_FLOOR` and saturation is increased (capped at 100).
 *
 * Low-saturation colors (S < `LOW_SAT_THRESHOLD`) skip saturation adjustments
 * to avoid introducing unwanted color cast into near-grays.
 *
 * Returns `baseHex` unchanged if `targetIndex === anchorIndex` or if conversion fails.
 * @param baseHex - The base color hex string
 * @param targetIndex - Position to compute: 0=LIGHTER, 1=LIGHT, 2=MEDIUM, 3=DARK, 4=DARKER
 * @returns Hex string for the requested shade position
 */
export function computeShadeHex(baseHex, targetIndex) {
  if (!baseHex || !isValidHex(baseHex)) return baseHex;
  const rgb = convertHexAndAlphaToRgbaObject(baseHex);
  const hsb = convertRgbToHsb(rgb);
  if (!hsb || !isValidHsbObject(hsb)) return baseHex;
  const {
    h,
    s: baseS,
    b: baseB
  } = hsb;
  const anchorIndex = getAnchorPosition(baseHex);
  if (targetIndex === anchorIndex) return baseHex;
  const isLowSat = baseS < LOW_SAT_THRESHOLD;
  let targetB;
  let targetS;
  if (targetIndex < anchorIndex) {
    const effectiveCeiling = BRIGHTNESS_CEILING + (HSB_MAX_VALUES.b - BRIGHTNESS_CEILING) * (baseS / HSB_MAX_VALUES.s);
    const usableLighterHeadroom = Math.max(0, effectiveCeiling - baseB);
    const rawBPerStep = anchorIndex > 0 ? usableLighterHeadroom / anchorIndex : 0;
    const bPerStep = Math.min(MAX_LIGHTER_B_STEP, rawBPerStep);
    const stepsAway = anchorIndex - targetIndex;
    targetB = Math.min(effectiveCeiling, baseB + stepsAway * bPerStep);
    if (isLowSat) {
      targetS = baseS;
    } else if (usableLighterHeadroom === 0) {
      // B is already at ceiling — no brightness headroom, so drive lighter stops
      // through saturation alone, distributing from baseS down to SAT_FLOOR
      const maxSStep = anchorIndex <= 1 ? MAX_LIGHTER_S_STEP_FROM_LIGHT : MAX_LIGHTER_S_STEP_FROM_MEDIUM;
      const rawSPerStep = anchorIndex > 0 ? (baseS - SAT_FLOOR) / anchorIndex : 0;
      const sPerStep = Math.min(maxSStep, rawSPerStep);
      targetS = Math.max(SAT_FLOOR, baseS - stepsAway * sPerStep);
    } else {
      const maxSStep = anchorIndex <= 1 ? MAX_LIGHTER_S_STEP_FROM_LIGHT : MAX_LIGHTER_S_STEP_FROM_MEDIUM;
      targetS = Math.max(SAT_FLOOR, baseS + stepsAway * Math.max(-maxSStep, LIGHTER_SATURATION_STEP));
    }
  } else {
    const usableDarkerHeadroom = Math.max(0, baseB - BRIGHTNESS_FLOOR);
    const stepsAvailable = NUM_POSITIONS - 1 - anchorIndex;
    const bPerStep = stepsAvailable > 0 ? Math.min(MAX_DARKER_B_STEP, usableDarkerHeadroom / stepsAvailable) : 0;
    const stepsAway = targetIndex - anchorIndex;
    targetB = Math.max(BRIGHTNESS_FLOOR, baseB - stepsAway * bPerStep);
    targetS = isLowSat ? baseS : Math.min(HSB_MAX_VALUES.s, baseS + stepsAway * Math.min(MAX_DARKER_S_STEP, DARKER_SATURATION_STEP));
  }
  const newRgb = convertHsbToRgb({
    h,
    s: targetS,
    b: targetB
  });
  return newRgb && convertRgbToHex(newRgb) || baseHex;
}

/**
 * Returns the full 5-stop shade scale for a base hex color as an array of hex strings.
 * Index 0 = LIGHTER, 1 = LIGHT, 2 = MEDIUM, 3 = DARK, 4 = DARKER.
 * The element at `getAnchorPosition(hex)` is always the original `hex` value.
 * @param hex - Base hex color string
 * @returns Array of 5 hex strings, one per shade position
 */
export function getShadeScale(hex) {
  return Array.from({
    length: NUM_POSITIONS
  }, (_, i) => computeShadeHex(hex, i));
}

/** Maps shade name strings to their 0-based position index in the shade scale. */
const SHADE_POSITION_INDEX = Object.fromEntries(Object.values(ColorShadePositions).map((name, i) => [name, i]));

/**
 * Resolves a shade name (e.g., `"LIGHTER"`, `"DARK"`) to its computed hex color
 * for the given base hex color.
 * Returns `hex` unchanged if `shadeName` is not a recognized position name.
 * @param hex - Base hex color string
 * @param shadeName - Shade position name: `"LIGHTER"`, `"LIGHT"`, `"MEDIUM"`, `"DARK"`, or `"DARKER"`
 * @returns Hex string for the resolved shade, or `hex` if `shadeName` is unrecognized
 */
export function resolvePositionalShade(hex, shadeName) {
  const index = SHADE_POSITION_INDEX[shadeName];
  if (index === undefined) return hex;
  return computeShadeHex(hex, index);
}